import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { StreamService } from '../../services/stream.service';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'app-assessment-settings',
  templateUrl: './assessment-settings.component.html',
  styleUrls: ['./assessment-settings.component.scss']
})
export class AssessmentSettingsComponent implements OnInit, OnDestroy {
  @ViewChild('videoPlayer') videoPlayer!: ElementRef<HTMLVideoElement>;
  @ViewChild('videoWrapper') videoWrapperEl!: ElementRef;
  @ViewChild('audioPlayer') audioPlayer!: ElementRef<HTMLAudioElement>;
	@Input() lang: string = 'en';

  @Output() nextButton = new EventEmitter<boolean>();

  videoDevices: MediaDeviceInfo[] = [];
  audioInputDevices: MediaDeviceInfo[] = [];
  audioOutputDevices: MediaDeviceInfo[] = [];

  selectedCamera: string | undefined = undefined;
  selectedMicrophone: string | undefined = undefined;
  selectedSpeaker: string | undefined = undefined;

  muteMic = true;
  testSpeakers = false;
  audioMeterInterval: any;
	ngSelectLang = '';

	private videoStream!: MediaStream;

	passConditionsObserve!: Observable<boolean>;
	passConditionObservable!: Subscription;

	counter = 0;

  constructor(public streamService: StreamService) {}

  ngOnInit() {
		this.passConditionsObserve = this.streamService.passConditions$;
		if (this.lang === 'he') {
			this.ngSelectLang = 'ng-select-rtl';
		}
		this.nextButton.emit(false);
		this.getMediaDevices();
  }

	ngOnDestroy(): void {
		if (this.videoStream) {
      this.videoStream.getTracks().forEach(track => track.stop());
    }
    
    if (this.videoPlayer && this.videoPlayer.nativeElement) {
      this.videoPlayer.nativeElement.srcObject = null;
    }

		this.passConditionObservable.unsubscribe();
		this.streamService.stopStreaming();
	}

  getMediaDevices() {
    navigator.mediaDevices.getUserMedia({ audio: true, video: true })
		.then(stream => {
			navigator.mediaDevices.enumerateDevices()
				.then(devices => {
					this.videoDevices = devices.filter(device => device.kind === 'videoinput');
					this.audioInputDevices = devices.filter(device => device.kind === 'audioinput');
					this.audioOutputDevices = devices.filter(device => device.kind === 'audiooutput');
					this.selectedCamera = this.videoDevices[0]?.deviceId || undefined;
					this.selectedMicrophone = this.audioInputDevices[0]?.deviceId || undefined;
					this.selectedSpeaker = this.audioOutputDevices[0]?.deviceId || undefined;

					this.startVideoStream();
					this.startMicrophoneStream();
				});

			// Stop the stream immediately (not needed for device enumeration)
			stream.getTracks().forEach(track => track.stop());
		})
		.catch(error => console.error('Error accessing media devices:', error));
  }

  async startVideoStream() {
    if (this.selectedCamera) {
      navigator.mediaDevices.getUserMedia({ video: { deviceId: this.selectedCamera } })
        .then(async (stream) => {
					this.videoStream = stream;
					this.streamService.selectedCamera = this.selectedCamera;

					this.videoPlayer.nativeElement.srcObject = this.videoStream;
					this.videoPlayer.nativeElement.style.objectFit = 'cover';
          this.checkFrameRate();
					await this.streamService.checkConditions(this.videoPlayer.nativeElement);

					this.passConditionObservable = this.passConditionsObserve.subscribe(pass => {
						this.nextButton.emit(pass);
					});
        })
        .catch(error => console.error('Error accessing camera:', error));
    }
  }

  startMicrophoneStream() {
    if (this.selectedMicrophone) {
      navigator.mediaDevices.getUserMedia({ audio: { deviceId: this.selectedMicrophone } })
			.then(stream => {
				this.videoStream = stream;
				this.streamService.selectedMicrophone = this.selectedMicrophone;

				const audioContext = new AudioContext();
				const analyser = audioContext.createAnalyser();
				const microphone = audioContext.createMediaStreamSource(this.videoStream);
				microphone.connect(analyser);

				this.audioMeterInterval = setInterval(() => {
					const bufferLength = analyser.frequencyBinCount;
					const dataArray = new Uint8Array(bufferLength);
					analyser.getByteFrequencyData(dataArray);
					this.updateAudioMeter(dataArray);
				}, 50); // Update every 50 milliseconds
			})
			.catch(error => console.error('Error accessing microphone:', error));
    }
  }

  updateAudioMeter(dataArray: Uint8Array) {
    const allPids = document.querySelectorAll('.pid');
    const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
    const numPidsToColor = Math.round(average / 10); // Adjust this for sensitivity

    allPids.forEach((pid, index) => {
      if (index < numPidsToColor) {
        pid.classList.add('bg-green');
      } else {
        pid.classList.remove('bg-green');
      }
    });
  }

	checkFrameRate() {
		if (this.videoPlayer.nativeElement.srcObject instanceof MediaStream) {
			const videoTracks = this.videoPlayer.nativeElement.srcObject.getVideoTracks();
	
			if (videoTracks && videoTracks.length > 0) { // Check if video tracks exist
				const videoTrack = videoTracks[0];
				const settings = videoTrack.getSettings();
				const actualFrameRate = settings.frameRate; // Safe to access now
				
				if (actualFrameRate !== undefined) { // Check if frameRate is defined
					this.streamService.frameRate = actualFrameRate;
					const minFrameRate = 25;
					this.nextButton.emit(actualFrameRate >= minFrameRate);
		
					if (actualFrameRate < minFrameRate) {
						console.error('Camera does not meet minimum frame rate requirement.');
						// You can display a more user-friendly error message here
					}
				} else {
					console.warn('Frame rate is undefined.');
					// You might want to handle this case specifically
				}
			} else {
				console.warn('No video tracks found.');
				// You can display a message to the user indicating no video is available
			}
		} else {
			console.warn('Video source is not a MediaStream, cannot check frame rate.');
		}
	}

  @HostListener('window:resize')
  resizeWindow() {
    const videoWidth = this.videoWrapperEl.nativeElement.offsetWidth;
    const videoHeight = videoWidth * 0.5625; // Maintain 16:9 aspect ratio
    this.videoPlayer.nativeElement.style.width = `${videoWidth}px`;
    this.videoPlayer.nativeElement.style.height = `${videoHeight}px`;
  }

  playsound() {
		if (this.selectedSpeaker) {
			if (this.testSpeakers) {
				this.audioPlayer.nativeElement.pause();
				this.testSpeakers = false;
			} else {
				this.testSpeakers = true;
				this.audioPlayer.nativeElement.currentTime = 0;
				this.audioPlayer.nativeElement.src = "./../../../assets/sound_check.mp3";
				this.audioPlayer.nativeElement.setSinkId(this.selectedSpeaker)
					.then(() => this.audioPlayer.nativeElement.play())
					.catch(error => console.error('Error setting audio output:', error));
			}
		}
  }

  muteStatusChange() {
    this.muteMic = !this.muteMic;
    if (this.muteMic) {
      this.audioPlayer.nativeElement.pause(); // Stop audio if mic is muted
    }
  }
}
